import useUserFeedback from '@/helper/useUserFeedback';
import { useSnackbar } from 'notistack';
import { useCallback } from 'react';

export const UNREACHABLE = 'The server cannot be reached right now. Please try again in a moment.';
export const EXPIRED = 'Authentication expired';
export const LOGIN_FAILED = 'Authentication failed';
export const LOGIN_NOT_SAVED =
  'You are logged in, but your browser did not let us save the login. Other open tabs will not pick it up, and you ' +
  'may have to log in again after reloading the page.';
export const LOGOUT_NOT_SAVED =
  'You are logged out here, but your browser did not let us save that. Other open tabs may stay logged in.';

// Every error notice about the session uses this key, so that a newer one
// replaces the last and a login closes it. Warnings stay: they describe a
// session that took effect.
const SESSION_ERROR_NOTICE = 'session-error';

interface SessionNotices {
  notifyError: (text: string) => void;
  notifyWarning: (text: string) => void;
  closeErrorNotice: () => void;
}

/** The notices the user gets about logging in, the session and logging out. */
export default function useSessionNotices(): SessionNotices {
  const { enqueueFeedback } = useUserFeedback();
  const { closeSnackbar } = useSnackbar();

  const notifyError = useCallback(
    (text: string): void => enqueueFeedback(text, { variant: 'error', key: SESSION_ERROR_NOTICE }),
    [enqueueFeedback],
  );

  const notifyWarning = useCallback(
    (text: string): void => enqueueFeedback(text, { variant: 'warning' }),
    [enqueueFeedback],
  );

  const closeErrorNotice = useCallback((): void => closeSnackbar(SESSION_ERROR_NOTICE), [closeSnackbar]);

  return { notifyError, notifyWarning, closeErrorNotice };
}
